<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card mb-4">
          <div class="card-header pb-0">
            <h6>認證管理</h6>
            <!-- Filters -->
            <div class="row mt-3">
              <div class="col-md-3">
                <label>認證編號</label>
                <soft-input
                  v-model="filters.certification_number"
                  placeholder="搜尋認證編號"
                  icon="search"
                />
              </div>
              <div class="col-md-3">
                <label>訂單編號</label>
                <soft-input
                  v-model="filters.order_number"
                  placeholder="搜尋訂單編號"
                  icon="search"
                />
              </div>
              <div class="col-md-3">
                <label>狀態</label>
                <select class="form-control" v-model="filters.status">
                  <option value="">所有狀態</option>
                  <option value="pending">待處理</option>
                  <option value="platform_verifying">驗證中</option>
                  <option value="platform_pending_shipment">待出貨</option>
                  <option value="verification_failed">失敗</option>
                </select>
              </div>
              <div class="col-md-3">
                <label>賣家電子郵件</label>
                <soft-input
                  v-model="filters.seller_email"
                  placeholder="搜尋賣家電子郵件"
                  icon="email"
                />
              </div>
            </div>
            <div class="row mt-3 mb-3">
              <div class="col-md-12 d-flex justify-content-end">
                <soft-button
                  color="primary"
                  size="sm"
                  class="me-2"
                  @click="fetchCertifications"
                  >搜尋</soft-button
                >
                <soft-button color="secondary" size="sm" @click="resetFilters"
                  >重置</soft-button
                >
              </div>
            </div>
          </div>
          <div class="card-body px-0 pt-0 pb-2">
            <div class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      認證編號
                    </th>
                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2"
                    >
                      訂單編號
                    </th>
                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2"
                    >
                      商品
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      狀態
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      結果
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      賣家
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      買家
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      建立時間
                    </th>
                    <th class="text-secondary opacity-7"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loading">
                    <td colspan="9" class="text-center py-4">
                      <div class="spinner-border text-success" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                      <p class="mt-2 text-sm text-secondary">載入中...</p>
                    </td>
                  </tr>
                  <template v-else>
                    <tr v-for="cert in certifications" :key="cert.id">
                      <td>
                        <div class="d-flex px-2 py-1">
                          <div
                            class="d-flex flex-column justify-content-center"
                          >
                            <h6 class="mb-0 text-sm">
                              #{{ cert.certification_number }}
                            </h6>
                          </div>
                        </div>
                      </td>
                      <td>
                        <p class="text-xs font-weight-bold mb-0">
                          #{{ cert.order ? cert.order.order_number : "N/A" }}
                        </p>
                      </td>
                      <td>
                        <div class="d-flex px-2 py-1">
                          <div>
                            <img
                              :src="
                                (cert.product && cert.product.image) ||
                                '/no-image.png'
                              "
                              class="avatar avatar-sm me-3"
                              alt="product"
                            />
                          </div>
                          <div
                            class="d-flex flex-column justify-content-center"
                          >
                            <h6 class="mb-0 text-sm">
                              {{ cert.product ? cert.product.name : "Unknown" }}
                            </h6>
                          </div>
                        </div>
                      </td>
                      <td class="align-middle text-center text-sm">
                        <soft-badge
                          :color="getStatusColor(cert.status)"
                          variant="gradient"
                          size="sm"
                        >
                          {{ formatStatus(cert.status) }}
                        </soft-badge>
                      </td>
                      <td class="align-middle text-center text-sm">
                        <span
                          v-if="cert.result === 'success'"
                          class="text-success text-xs font-weight-bold"
                          >成功</span
                        >
                        <span
                          v-else-if="cert.result === 'failed'"
                          class="text-danger text-xs font-weight-bold"
                          >失敗</span
                        >
                        <span v-else class="text-secondary text-xs">-</span>
                      </td>
                      <td class="align-middle text-center text-sm">
                        <p
                          class="text-xs font-weight-bold mb-0 cursor-pointer text-primary"
                          @click="showSellerInfo(cert.seller)"
                        >
                          {{ cert.seller ? cert.seller.name : "未知" }}
                        </p>
                      </td>
                      <td class="align-middle text-center text-sm">
                        <p
                          class="text-xs font-weight-bold mb-0 cursor-pointer text-primary"
                          @click="showBuyerInfo(cert.buyer)"
                        >
                          {{ cert.buyer ? cert.buyer.name : "未知" }}
                        </p>
                      </td>
                      <td class="align-middle text-center">
                        <span class="text-secondary text-xs font-weight-bold">{{
                          new Date(cert.created_at).toLocaleString()
                        }}</span>
                      </td>
                      <td class="align-middle">
                        <div class="dropdown">
                          <button
                            class="btn btn-link text-secondary mb-0"
                            data-bs-toggle="dropdown"
                          >
                            <i class="fas fa-ellipsis-v"></i>
                          </button>
                          <ul class="dropdown-menu">
                            <li v-if="cert.status === 'platform_verifying'">
                              <a
                                class="dropdown-item text-success"
                                href="javascript:;"
                                @click="updateResult(cert, 'success')"
                                >認證成功</a
                              >
                            </li>
                            <li v-if="cert.status === 'platform_verifying'">
                              <a
                                class="dropdown-item text-danger"
                                href="javascript:;"
                                @click="updateResult(cert, 'failed')"
                                >認證失敗</a
                              >
                            </li>
                            <li
                              v-if="cert.status === 'platform_pending_shipment'"
                            >
                              <a
                                class="dropdown-item text-primary"
                                href="javascript:;"
                                @click="openShipModal(cert)"
                                >出貨給買家</a
                              >
                            </li>
                            <li v-if="cert.status === 'verification_failed'">
                              <a
                                class="dropdown-item text-danger"
                                href="javascript:;"
                                @click="cancelOrder(cert)"
                                >取消訂單</a
                              >
                            </li>
                            <li>
                              <a
                                class="dropdown-item"
                                href="javascript:;"
                                @click="
                                  $router.push(`/orders/${cert.order_id}`)
                                "
                                >查看訂單</a
                              >
                            </li>
                          </ul>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="certifications.length === 0">
                      <td colspan="9" class="text-center py-4 text-secondary">
                        <i class="ni ni-folder-17 text-lg mb-2 d-block"></i>
                        找不到認證資料
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
            <!-- Pagination -->
            <div
              class="d-flex justify-content-center mt-3"
              v-if="pagination.total > 0"
            >
              <soft-pagination color="success">
                <soft-pagination-item
                  prev
                  @click="changePage(pagination.current_page - 1)"
                  :disabled="pagination.current_page === 1"
                />
                <soft-pagination-item
                  label
                  v-for="page in pagination.last_page"
                  :key="page"
                  :active="page === pagination.current_page"
                  @click="changePage(page)"
                  >{{ page }}</soft-pagination-item
                >
                <soft-pagination-item
                  next
                  @click="changePage(pagination.current_page + 1)"
                  :disabled="pagination.current_page === pagination.last_page"
                />
              </soft-pagination>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Ship Modal -->
    <div
      v-if="showShipModal"
      class="modal fade show d-block"
      style="background: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">出貨給買家</h5>
            <button type="button" class="btn-close" @click="closeShipModal">
              X
            </button>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <label>物流商</label>
              <input
                type="text"
                class="form-control"
                v-model="shipForm.logistics_provider"
              />
            </div>
            <div class="form-group">
              <label>追蹤號碼</label>
              <input
                type="text"
                class="form-control"
                v-model="shipForm.tracking_number"
              />
            </div>
            <div class="form-group">
              <label>影片證據 (選填)</label>
              <input
                type="file"
                class="form-control"
                @change="handleFileUpload"
              />
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeShipModal"
            >
              關閉
            </button>
            <button type="button" class="btn btn-primary" @click="submitShip">
              確認出貨
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SoftBadge from "@/components/SoftBadge.vue";
import SoftButton from "@/components/SoftButton.vue";
import SoftInput from "@/components/SoftInput.vue";
import SoftPagination from "@/components/SoftPagination.vue";
import SoftPaginationItem from "@/components/SoftPaginationItem.vue";
import request from "@/utils/request";
import Swal from "sweetalert2";

export default {
  name: "Certifications",
  components: {
    SoftBadge,
    SoftButton,
    SoftInput,
    SoftPagination,
    SoftPaginationItem,
  },
  data() {
    return {
      certifications: [],
      loading: false,
      filters: {
        certification_number: "",
        order_number: "",
        status: "",
        seller_email: "",
      },
      pagination: {
        current_page: 1,
        last_page: 1,
        total: 0,
        per_page: 10,
      },
      showShipModal: false,
      selectedCert: null,
      shipForm: {
        logistics_provider: "",
        tracking_number: "",
        file: null,
      },
    };
  },
  methods: {
    getStatusColor(status) {
      switch (status) {
        case "platform_pending_shipment":
          return "info";
        case "platform_verifying":
          return "warning";
        case "verification_failed":
          return "danger";
        case "completed":
          return "success";
        default:
          return "secondary";
      }
    },
    formatStatus(status) {
      const statusMap = {
        pending: "待處理",
        platform_verifying: "平台驗證中",
        platform_pending_shipment: "平台待出貨",
        verification_failed: "驗證失敗",
        completed: "已完成",
        cancelled: "已取消",
      };
      return statusMap[status] || status;
    },
    async fetchCertifications(page = 1) {
      this.loading = true;
      try {
        const params = {
          page,
          ...this.filters,
        };
        Object.keys(params).forEach(
          (key) =>
            (params[key] === "" || params[key] === null) && delete params[key]
        );

        const response = await request.get("/api/admin/certifications", {
          params,
        });
        this.certifications = response.data.data || [];
        this.pagination = response.data.meta || {
          current_page: 1,
          last_page: 1,
          total: 0,
          per_page: 10,
        };
      } catch (error) {
        console.error("Failed to fetch certifications", error);
        this.$swal.fire("錯誤", "載入數據失敗", "error");
      } finally {
        this.loading = false;
      }
    },
    resetFilters() {
      this.filters = {
        certification_number: "",
        order_number: "",
        status: "",
        seller_email: "",
      };
      this.fetchCertifications(1);
    },
    changePage(page) {
      if (page >= 1 && page <= this.pagination.last_page) {
        this.fetchCertifications(page);
      }
    },
    showSellerInfo(seller) {
      if (seller) {
        Swal.fire({
          title: "賣家資訊",
          html: `<p>賣家: ${seller.name}</p><p>電子郵件: ${
            seller.email
          }</p><p>電話: ${seller.phone || "無"}</p>`,
          icon: "info",
        });
      }
    },
    showBuyerInfo(buyer) {
      if (buyer) {
        Swal.fire({
          title: "買家資訊",
          html: `<p>買家: ${buyer.name}</p><p>電子郵件: ${
            buyer.email
          }</p><p>電話: ${buyer.phone || "無"}</p>`,
          icon: "info",
        });
      }
    },
    async updateResult(cert, result) {
      this.$swal({
        title: `將認證標記為 ${result === "success" ? "成功" : "失敗"}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "確定",
        cancelButtonText: "取消",
      }).then(async (res) => {
        if (res.isConfirmed) {
          try {
            await request.post(`/api/admin/certifications/${cert.id}/result`, {
              result,
            });
            this.fetchCertifications(this.pagination.current_page);
            this.$swal.fire("成功", "認證結果已更新", "success");
          } catch (error) {
            this.$swal.fire("錯誤", "更新結果失敗", "error");
          }
        }
      });
    },
    async cancelOrder(cert) {
      this.$swal({
        title: "您確定要取消相關訂單嗎？",
        text: "此操作無法復原",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ea0606",
        confirmButtonText: "確定取消",
        cancelButtonText: "取消",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await request.post(`/api/admin/orders/${cert.order_id}/cancel`);
            this.fetchCertifications(this.pagination.current_page);
            this.$swal.fire("已取消", "訂單已成功取消", "success");
          } catch (error) {
            this.$swal.fire("錯誤", "取消訂單失敗", "error");
          }
        }
      });
    },
    openShipModal(cert) {
      this.selectedCert = cert;
      this.shipForm = {
        logistics_provider: "",
        tracking_number: "",
        file: null,
      };
      this.showShipModal = true;
    },
    closeShipModal() {
      this.showShipModal = false;
      this.selectedCert = null;
    },
    handleFileUpload(event) {
      this.shipForm.file = event.target.files[0];
    },
    async submitShip() {
      if (!this.shipForm.logistics_provider || !this.shipForm.tracking_number) {
        this.$swal.fire("提示", "請填寫所有必填欄位", "warning");
        return;
      }

      const formData = new FormData();
      formData.append("logistics_provider", this.shipForm.logistics_provider);
      formData.append("tracking_number", this.shipForm.tracking_number);
      if (this.shipForm.file) formData.append("video", this.shipForm.file);

      try {
        await request.post(
          `/api/admin/certifications/${this.selectedCert.id}/ship`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        this.closeShipModal();
        this.fetchCertifications(this.pagination.current_page);
        this.$swal.fire("成功", "出貨成功", "success");
      } catch (error) {
        this.$swal.fire("錯誤", "出貨失敗", "error");
      }
    },
  },
  created() {
    this.fetchCertifications();
  },
};
</script>
